import { CustomField, CustomFieldType, CustomFieldValue } from '../../../../../types/custom_field';
import { Order } from '../../../../../types/order';
import { ProductTransaction } from '../../../../../types/productTransaction';
import { System } from '../../../../../types/system';
import { PdfDataKey, PdfFieldEntity } from '../PdfDataKey';
import { PdfEntityDataResolver } from '../PdfEntityDataResolver';

export enum PdfOrderProperty {
  number = 'number',
  updatedAt = 'updatedAt',
  containerQuantity = 'containerQuantity',
  processedContainerQuantity = 'processedContainerQuantity',
  totalQuantity = 'totalQuantity',
  totalProcessedQuantity = 'totalProcessedQuantity',
}

export class PdfOrderDataResolver extends PdfEntityDataResolver<Order, PdfOrderProperty> {
  properties = Object.values(PdfOrderProperty);
  entityType = PdfFieldEntity.order;

  constructor(private readonly productTransactions: Map<string, ProductTransaction>) {
    super();
  }

  isDataKeyValidForImage(key: PdfDataKey<PdfOrderProperty>, customFields: Map<string, CustomField>): boolean {
    if ('customFieldId' in key) {
      const customField = customFields.get(key.customFieldId);
      if (!customField) return false;
      return customField.type === CustomFieldType.image;
    }

    return false;
  }

  getCustomFieldByEntity(entity: Order, id: string): CustomFieldValue | undefined {
    return entity.customFields.get(id);
  }

  getLabelByProperty(key: PdfOrderProperty): string {
    switch (key) {
      case PdfOrderProperty.number:
        return 'Order Number';
      case PdfOrderProperty.containerQuantity:
        return 'Number of containers';
      case PdfOrderProperty.processedContainerQuantity:
        return 'Number of processed containers';
      case PdfOrderProperty.totalQuantity:
        return 'Total quantity';
      case PdfOrderProperty.totalProcessedQuantity:
        return 'Total processed quantity';
      case PdfOrderProperty.updatedAt:
        return 'Last updated on';
    }
  }

  getValueByProperty(key: PdfOrderProperty, order: Order): string {
    switch (key) {
      case PdfOrderProperty.updatedAt:
        return new Date(order.updatedAt).toLocaleDateString();
      case PdfOrderProperty.number:
        return order.number || '';
      case PdfOrderProperty.containerQuantity:
        return [...this.productTransactions.values()]
          .filter(
            pt =>
              pt.parentId === order.id &&
              pt.statusId === System.ProductTransactionStatus.completedId &&
              pt.product.containerId,
          )
          .length.toString();
      case PdfOrderProperty.processedContainerQuantity:
        return [...this.productTransactions.values()]
          .filter(pt => pt.parentId === order.id && pt.product.containerId)
          .length.toString();
      case PdfOrderProperty.totalQuantity:
        return [...this.productTransactions.values()]
          .filter(pt => pt.parentId === order.id && pt.product.pmdId)
          .reduce((sum, pt) => sum + pt.product.quantity, 0)
          .toString();
      case PdfOrderProperty.totalProcessedQuantity:
        return [...this.productTransactions.values()]
          .filter(
            pt =>
              pt.parentId === order.id &&
              pt.statusId === System.ProductTransactionStatus.completedId &&
              pt.product.pmdId,
          )
          .reduce((sum, pt) => sum + pt.product.quantity, 0)
          .toString();
    }
  }
}
